import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { fetchProducts, fetchShopifyProducts } from "@/lib/api";

const Signature = () => {
    const [items, setItems] = useState([]);
    const track = useRef(null);

    useEffect(() => {
        fetchShopifyProducts({ signature: "true" })
            .then((d) => (d.items.length ? d.items : fetchProducts({ signature: "true", limit: 10 }).then((l) => l.items)))
            .then((list) => setItems(list))
            .catch(() => fetchProducts({ signature: "true", limit: 10 }).then((d) => setItems(d.items)).catch(() => {}));
    }, []);

    const scrollBy = (dir) => {
        track.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
    };

    if (!items.length) return null;

    return (
        <section className="py-24 sm:py-36 overflow-hidden" data-testid="signature-showcase">
            <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
                <div className="flex items-end justify-between gap-6">
                    <SectionHeading
                        align="left"
                        eyebrow="Signature Pieces"
                        title={<>Designed to Be <span className="italic gold-text">Remembered</span></>}
                    />
                    <div className="hidden sm:flex gap-3 mb-14">
                        <button onClick={() => scrollBy(-1)} data-testid="signature-prev-btn" aria-label="Previous" className="w-11 h-11 border border-gold/30 text-gold flex items-center justify-center hover:bg-gold hover:text-obsidian transition-colors">
                            <ChevronLeft size={18} strokeWidth={1.5} />
                        </button>
                        <button onClick={() => scrollBy(1)} data-testid="signature-next-btn" aria-label="Next" className="w-11 h-11 border border-gold/30 text-gold flex items-center justify-center hover:bg-gold hover:text-obsidian transition-colors">
                            <ChevronRight size={18} strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            </div>
            <div
                ref={track}
                className="flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory px-5 sm:px-8 pb-4"
                data-testid="signature-gallery"
            >
                {items.map((p) => (
                    <Link
                        key={p.slug}
                        to={`/product/${p.slug}`}
                        data-testid={`signature-item-${p.slug}`}
                        className="group relative shrink-0 w-[260px] sm:w-[320px] snap-start"
                    >
                        <div className="aspect-[3/4] overflow-hidden border border-gold/10 group-hover:border-gold/60 transition-colors duration-500">
                            <img
                                src={p.image}
                                alt={`${p.name} — signature piece at BSK Jewellers`}
                                loading="lazy"
                                className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.09]"
                            />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="absolute bottom-0 inset-x-0 p-6 opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500">
                            <p className="text-[10px] tracking-[0.3em] uppercase text-gold">{p.category}</p>
                            <h3 className="font-serif text-2xl text-ivory mt-1">{p.name}</h3>
                            <span className="inline-block text-[10px] tracking-[0.25em] uppercase text-sand mt-3 border-b border-gold/50 pb-0.5">View Details</span>
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
};

export default Signature;
